import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import {
  addCity,
  CityCells,
  deleteCity,
  editCity,
  getCities,
} from "framework/cities";
import { getCountries } from "framework/countries";
import { Feature } from "models/api";
import { ICity, ICityInput } from "models/city";
import { ICountry } from "models/country";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import AddCity from "./partials/AddCity/AddCity";
import EditCity from "./partials/EditCity/EditCity";
import DeleteCity from "./partials/DeleteCity/DeleteCity";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";

interface Props {
  feature: Feature;
}

const Cities: React.FC<Props> = ({ feature }) => {
  const [loading4, setLoading4] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [countries, setCountries] = useState<ICountry[]>([] as ICountry[]);
  const [FilterCities, setFilterCities] = useState<ICity[]>([] as ICity[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const user = getUserData();
  const { colors } = user;
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCity, setSelectedCity] = useState<ICity>({} as ICity);
  const [inputObj, setInputObj] = useState<ICityInput>({} as ICityInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addCitiesMutation = useMutation({
    mutationFn: (createInput: ICityInput) => {
      return addCity(createInput);
    },
  });

  const editCitiesMutation = useMutation({
    mutationFn: (data: { createInput: ICityInput; id: string }) => {
      return editCity(data.createInput, data.id);
    },
  });

  const deleteCitiesMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCity(id);
    },
  });

  const fetchCountries = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get countries", () =>
        getCountries()
      );
      setCountries(response);
      setLoading4(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading4(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchCountries();
  }, []);

  // const { loading:loadingFilters, filters, error:errorFilters, revalidate:revalidateFilters } = useFetchMultipleFilters({
  //   fetchFilters: {
  //     countries: getCountries,
  //     cities: getCities,
  //   },
  //   // processFilters: {
  //   //   countries: (data) => data.filter((country) => country.active),
  //   //   cities: (data) => data.filter((city) => city.active),
  //   // },
  //   onSuccess: (data) => {
  //     console.log("Filters fetched:", data);
  //   },
  // });

  // const { loading:loadingFilters, filters, error:errorFilters } = useFetchMultipleFilters({
  //   fetchFilters: {
  //     countries: getCountries,
  //     cities: getCities,
  //   },onSuccess: (data) => {
  //     console.log("Filters fetched:", data);
  //   }

  // });

  const {
    loading,
    data: Cities,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCities(),
    fetchPermission: () => getPermission(1),
    section: "Cities",
    onSuccess: (data) => setFilterCities(data),
    processResponse: (data) =>
      data.map((item: ICity) => {
        return { ...item, id: item.cityId };
      }),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (City: ICity) => {
    setSelectedCity(City);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCity({} as ICity);
  };

  const handleOpenDelete = (City: ICity) => {
    setSelectedCity(City);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCity({} as ICity);
  };

  const getBodyCells = (row: ICity) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "cityName")
        return {
          id: "cityName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.cityName}</Typography>
            </TableCell>
          ),
        };

      if (key === "cityNameAr")
        return {
          id: "cityNameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.cityNameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "countryCode")
        return {
          id: "countryCode",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.countryCode}</Typography>
            </TableCell>
          ),
        };

      if (key === "countryName")
        return {
          id: "countryName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.countryName}</Typography>
            </TableCell>
          ),
        };

      if (key === "countryNameAr")
        return {
          id: "countryNameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.countryNameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name || !inputObj.countryId || !inputObj.nameAr)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addCitiesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "City added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as ICityInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: ICityInput = {
        name: inputObj.name || selectedCity.cityName,
        countryId: inputObj.countryId || selectedCity.countryId,
        nameAr: inputObj.nameAr || selectedCity.cityNameAr,
      };
      await editCitiesMutation.mutateAsync({
        createInput,
        id: selectedCity.cityId,
      });
      setAlert({
        open: true,
        message: "City updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as ICityInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCitiesMutation.mutateAsync(selectedCity.cityId);
      setAlert({
        open: true,
        message: "City deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCities(Cities!);
    let filtered = Cities!.filter((city) =>
      city.cityName.toLowerCase().includes(value.toLowerCase())
    );
    setFilterCities(filtered);
  };
  const handleSearch2 = async (code: string) => {
    if (!code) return setFilterCities(Cities!);
    // filter by country code
    let filtered = Cities!.filter((city) =>
      city.countryCode.toLowerCase().includes(code.toLowerCase())
    );
    setFilterCities(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography
              variant="h3"
              color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            >
              {t(feature.name)}
            </Typography>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_city")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={t("T_city")} />
              <Search
                handleChange={handleSearch2}
                placeholder={t("T_country_code")}
              />
            </Stack>
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterCities.length > 0 && (
          <List
            headCells={CityCells}
            data={FilterCities}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCities || FilterCities.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_city_header")}
              message={t("T_no_city_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCity
          open={openAdd}
          loading={loading2}
          countries={countries}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCity
          open={openEdit}
          loading={loading2}
          city={selectedCity}
          countries={countries}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCity
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Cities;
